import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "../api/client";

/**
 * Case-by-case create flow.
 *
 * Case-by-case is used to price a PROSPECTIVE placement, not for someone we
 * already support. So we don't pre-fill from the PWS table; the user enters
 * the prospect's initials (and optional name) freely. The home decides which
 * cost stack the calc engine uses, and the funder is needed for the quote
 * letter once approved.
 */
export function CaseNew() {
  const navigate = useNavigate();
  const homes   = useQuery({ queryKey: ["homes"],   queryFn: api.homes });
  const funders = useQuery({ queryKey: ["funders"], queryFn: api.funders });

  const [homeId,   setHomeId]   = useState<number | "">("");
  const [funderId, setFunderId] = useState<number | "">("");
  const [pwsInitials, setPwsInitials] = useState("");
  const [pwsName,     setPwsName]     = useState("");

  const create = useMutation({
    mutationFn: () => api.createCase({
      home_id:      Number(homeId),
      pws_id:       null, // prospective, not yet in the PWS table
      pws_initials: pwsInitials || "??",
      funder_id:    funderId === "" ? null : Number(funderId),
      // Default zeros, the editor on the next page surfaces them all.
      shared_hours_mode: "Shared day and night support",
      contribution_target_pct: 0.10,
      // Notes column carries the optional display name until CRM2 / Dynamics 365
      // lets us properly resolve a prospective placement to a PWS row.
      notes: pwsName ? `Prospect name: ${pwsName}` : null,
    }),
    onSuccess: (data) => navigate(`/case/${data.id}`),
  });

  const canContinue = homeId !== "" && funderId !== "" && pwsInitials.trim().length >= 2;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-liaise-navy">New case-by-case quote</h1>
        <p className="text-slate-500 mt-1">
          Step 1 of 2. Use this for a <b>prospective placement</b> where the funder needs a bespoke per-PWS quote.
          For an existing person we support, use a Holiday / Hospital / Transition quote instead.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
        <Row label="Home">
          <select className="input" value={homeId} onChange={(e) => setHomeId(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">Select a home...</option>
            {/* Only show homes that have a cost stack loaded for the active FY.
                Others would 422 the calc engine ("No cost stack for ..."). */}
            {(homes.data?.homes ?? []).filter((h: any) => h.has_cost_stack).map((h: any) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
          <div className="text-xs text-slate-500 mt-1">
            The home decides which management, overhead and regional cost stack the calc engine uses.
            {(homes.data?.homes ?? []).some((h: any) => !h.has_cost_stack) && (
              <> Homes without a cost stack for {homes.data?.fiscal_year ?? "the current FY"} are hidden until Finance loads their figures.</>
            )}
          </div>
        </Row>

        <Row label="Funder">
          <select className="input" value={funderId} onChange={(e) => setFunderId(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">Select a funder...</option>
            {(funders.data?.funders ?? []).map((f: any) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </Row>

        <div className="grid grid-cols-2 gap-4">
          <Row label="PWS initials">
            <input
              className="input"
              maxLength={20}
              placeholder="e.g. MS"
              value={pwsInitials}
              onChange={(e) => setPwsInitials(e.target.value.toUpperCase())}
            />
            <div className="text-xs text-slate-500 mt-1">Shown on the quote letter to the funder.</div>
          </Row>
          <Row label="PWS name (optional)">
            <input
              className="input"
              placeholder="e.g. Michael Smith"
              value={pwsName}
              onChange={(e) => setPwsName(e.target.value)}
            />
            <div className="text-xs text-slate-500 mt-1">Only for our internal records.</div>
          </Row>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          <button
            className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={create.isPending || !canContinue}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating..." : "Continue to inputs →"}
          </button>
          {!canContinue && (
            <span className="text-xs text-slate-500">Pick a home, funder and enter at least two-letter initials to continue.</span>
          )}
          {create.error && <span className="text-xs text-red-600">{(create.error as Error).message}</span>}
        </div>
      </div>

      <style>{`.input{display:block;width:100%;padding:.5rem .75rem;border:1px solid rgb(226 232 240);border-radius:.5rem;font-size:.875rem;background:white}`}</style>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 font-medium">{label}</div>
      {children}
    </label>
  );
}
